/**
 * Production Dashboard
 *
 * Real-time overview of production metrics and workflow status.
 * Displays stage cards, today's activity, historical performance chart, and active issues.
 *
 * Accessibility: ARIA live region announces updates to screen readers.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LayoutGrid,
  Package,
  Printer,
  Droplets,
  Film,
  Flame,
  Archive,
  PackageCheck,
  PackageOpen,
  Truck,
  AlertTriangle,
  RefreshCw,
  ChevronRight,
  CloudOff,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { MetricCard } from '@/features/production-orders/components/metrics';
import { DashboardHistoryChart } from '@/features/production-orders/components/DashboardHistoryChart';
import { DashboardDateRangePicker } from '@/features/production-orders/components/DashboardDateRangePicker';
import { ProductionCentrePicker } from '@/features/production-orders/components/ProductionCentrePicker';
import { getSelectedProductionCentre } from '@/features/production-orders/utils/production-centres';
import { useLocation } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useDashboard } from '@/features/production-orders/hooks/useDashboard';
import { trpc } from '@/lib/trpc';
import { useDashboardHistory } from '@/features/production-orders/hooks/useDashboardHistory';
import { useProductionSettings } from '@/features/production-settings/hooks/useProductionSettings';
import { computeVisibleIssueTotal, dashboardPrintFileCount } from '@/features/production-issues/issueTabs';
import { useIssueCounts } from '@/features/production-issues/hooks/useProductionIssues';
import { usePortlessBlockedCount } from '@/features/production-issues/hooks/usePortlessBlocked';
import { useShippingFailures } from '@/features/packing-station/hooks/useShipping';
import { cn } from '@/lib/utils';
import { formatDistanceToNow, subDays, format } from 'date-fns';
import type { DateRange } from '@/features/production-orders/types';

// Stage configuration with colors and icons
// Labels are i18n keys resolved at render time via t()
const STAGES = [
  { key: 'queue', labelKey: 'dashboard.stages.queue', color: 'bg-gray-50 dark:bg-gray-900/30', icon: Package },
  { key: 'picking', labelKey: 'dashboard.stages.picking', color: 'bg-amber-50 dark:bg-amber-900/30', icon: Package },
  { key: 'pretreat', labelKey: 'dashboard.stages.pretreat', color: 'bg-cyan-50 dark:bg-cyan-900/30', icon: Droplets },
  { key: 'printing', labelKey: 'dashboard.stages.printing', color: 'bg-purple-50 dark:bg-purple-900/30', icon: Printer },
  // Phase 50.01.5 — DTF stages (counted by get_dashboard_stats via garment
  // status FILM_PRINTED / PRESSED; DTG-only sites read 0). Film (sky) + Flame
  // (fuchsia) match the Batch Detail bar/cards.
  { key: 'filmPrinted', labelKey: 'dashboard.stages.filmPrinted', color: 'bg-sky-50 dark:bg-sky-900/30', icon: Film },
  { key: 'pressed', labelKey: 'dashboard.stages.pressed', color: 'bg-fuchsia-50 dark:bg-fuchsia-900/30', icon: Flame },
  { key: 'binned', labelKey: 'dashboard.stages.binned', color: 'bg-orange-50 dark:bg-orange-900/30', icon: Archive },
  { key: 'packing', labelKey: 'dashboard.stages.packing', color: 'bg-green-50 dark:bg-green-900/30', icon: PackageOpen },
  { key: 'shipped', labelKey: 'dashboard.stages.shipped', color: 'bg-emerald-100 dark:bg-emerald-900/30', icon: Truck },
] as const;

export default function ProductionDashboard() {
  const { t } = useTranslation('production');
  const {
    selectedLocation,
    setSelectedLocation,
    locations,
    isLoading: locationsLoading,
  } = useLocation();
  const selectedProductionCentre = getSelectedProductionCentre(locations, selectedLocation);
  const { organization } = useOrganization();
  const { data: stats, isLoading, error, refetch, isFetching } = useDashboard(selectedProductionCentre?.id);
  // Order sync only runs for orgs with a Shopify store attached. A storeless
  // org (e.g. a pure-fulfilment 3PL) has nothing to sync, so don't poll for or
  // surface sync health — it would read as a permanent "behind schedule" alarm.
  const hasShopifyStore = !!organization?.shopify_domain;
  // Sync health moves on the order-sync cron's 5 minute cadence, so polling it
  // faster than the data changes just re-counted the org's recent orders. Two
  // minutes still surfaces a stalled sync well inside the staleness threshold.
  const { data: syncStatus } = trpc.productionSettings.getSyncStatus.useQuery(undefined, {
    enabled: hasShopifyStore,
    refetchInterval: 120_000,
    refetchIntervalInBackground: false,
  });

  // Historical performance chart state (default to last 7 days)
  const [historyDateRange, setHistoryDateRange] = useState<DateRange>({
    from: subDays(new Date(), 7),
    to: new Date(),
  });

  // Fetch historical data for performance chart
  const {
    data: historyData,
    isLoading: isLoadingHistory,
  } = useDashboardHistory(selectedProductionCentre?.id, historyDateRange);

  // Per-user hidden issue tabs — hidden tabs drop out of the alert total so it
  // tracks what the Issues page actually shows this user. `settings` is
  // memoized by the hook, so the array reference is dep-stable.
  const hiddenIssueTabs = useProductionSettings().settings.hiddenIssueTabs;

  // Both halves of the Shipping tab badge (BLU-732). The stats payload carries
  // neither number, but the sidebar renders in the same viewport as this alert
  // and already fetches both — reading the shared cache here keeps the two
  // visible totals equal instead of disagreeing by the whole blocked backlog.
  const { data: shippingData } = useShippingFailures(selectedProductionCentre?.id ?? null);
  const shippingUnresolved = shippingData?.unresolvedCount ?? 0;
  const portlessBlocked = usePortlessBlockedCount(selectedProductionCentre?.id ?? null);
  // BLU-1439 (D-Q1): print files count only what needs a person, from the same
  // cached issue counts the sidebar fetches (the stats SQL counts every OPEN
  // row, including the ones the bridge is still fetching).
  const { data: issueCounts } = useIssueCounts(selectedProductionCentre?.id ?? '');

  const hasDashboardStats = stats != null;
  const oosCount = stats?.issues.oosCount ?? 0;
  const redoPending = stats?.issues.redoPending ?? 0;
  const stuckBins = stats?.issues.stuckBins ?? 0;
  const printFileIssues = dashboardPrintFileCount(issueCounts, stats?.issues.printFileIssues);
  const cancelledCount = stats?.issues.cancelledCount ?? 0;

  // Calculate total issues for alert badge — sum of the VISIBLE issue-tab
  // badges via the shared accessor. This payload has no split-orders /
  // on-hold fields, so those tabs never contribute here either way.
  // Use primitive values as dependencies to avoid re-computation on object reference changes
  const totalIssues = useMemo(() => {
    if (!hasDashboardStats) return 0;
    return computeVisibleIssueTotal(
      {
        oos_order_count: oosCount,
        redo_pending: redoPending,
        stuck_bins: stuckBins,
        print_file_issues: printFileIssues,
        cancelled_count: cancelledCount,
      },
      shippingUnresolved,
      hiddenIssueTabs,
      portlessBlocked,
    );
  }, [
    hasDashboardStats,
    oosCount,
    redoPending,
    stuckBins,
    printFileIssues,
    cancelledCount,
    hiddenIssueTabs,
    shippingUnresolved,
    portlessBlocked,
  ]);

  // Format last updated time
  const lastUpdatedText = useMemo(() => {
    if (!stats?.lastUpdated) return '';
    return formatDistanceToNow(new Date(stats.lastUpdated), { addSuffix: true });
  }, [stats?.lastUpdated]);

  // Sync health: healthy (<10min), stale (>15min), error (has lastError + stale)
  const syncHealth = useMemo(() => {
    // No store attached → no sync to report (guards stale cross-org cache too).
    if (!hasShopifyStore) return null;
    if (!syncStatus) return null;
    if (syncStatus.isRunning) return { status: 'running' as const, label: t('dashboard.syncStatus.syncing') };
    if (!syncStatus.lastSyncedAt) return { status: 'stale' as const, label: t('dashboard.syncStatus.neverSynced') };
    const ageMin = (Date.now() - new Date(syncStatus.lastSyncedAt).getTime()) / 60_000;
    const timeAgo = formatDistanceToNow(new Date(syncStatus.lastSyncedAt), { addSuffix: true });
    if (syncStatus.lastError && ageMin > 15)
      return { status: 'error' as const, label: t('dashboard.syncStatus.syncError', { time: timeAgo }) };
    if (ageMin > 15) return { status: 'stale' as const, label: t('dashboard.syncStatus.syncStale', { time: timeAgo }) };
    return { status: 'healthy' as const, label: t('dashboard.syncStatus.synced', { time: timeAgo }) };
  }, [syncStatus, hasShopifyStore, t]);

  // Production intentionally supports an "All Locations" sentinel. Use that
  // state as a centre launcher instead of silently choosing a floor for the
  // operator. Inventory-only and inactive locations are never accepted here.
  if (!selectedProductionCentre) {
    return (
      <ProductionCentrePicker
        locations={locations}
        isLoading={locationsLoading}
        onSelect={setSelectedLocation}
      />
    );
  }

  // Error state with retry
  if (error) {
    return (
      <div className="container mx-auto px-6 py-6 max-w-7xl">
        <Card className="border-destructive">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
            <p className="text-lg font-medium">{t('dashboard.failedToLoad')}</p>
            <p className="text-muted-foreground mb-4">{error.message}</p>
            <Button onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('dashboard.retry')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-6 max-w-7xl space-y-6 animate-enter">
      {/* ARIA Live Region for Screen Readers */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {isLoading && t('dashboard.loadingStats')}
        {!isLoading && stats && t('dashboard.statsUpdated', { queue: stats.stages.queue, shipped: stats.today.ordersShipped })}
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <LayoutGrid className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">{t('dashboard.title')}</h1>
            <p className="text-muted-foreground">
              {selectedProductionCentre.name} - {t('dashboard.realTimeOverview')}
            </p>
          </div>
        </div>

        {/* Live indicator + sync health - larger text for production floor visibility */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className={cn(
              "h-2.5 w-2.5 rounded-full bg-green-500",
              isFetching && "animate-pulse"
            )} />
            <span className="text-sm text-muted-foreground font-medium">{t('dashboard.live')}</span>
          </div>
          {lastUpdatedText && (
            <span className="text-xs text-muted-foreground">
              {t('dashboard.updated', { time: lastUpdatedText })}
            </span>
          )}
          {syncHealth && (
            <div className="flex items-center gap-1.5 border-l pl-4">
              {syncHealth.status === 'running' ? (
                <RefreshCw className="h-3.5 w-3.5 text-blue-500 animate-spin" />
              ) : syncHealth.status === 'healthy' ? (
                <div className="h-2.5 w-2.5 rounded-full bg-green-500" />
              ) : syncHealth.status === 'stale' ? (
                <div className="h-2.5 w-2.5 rounded-full bg-amber-500" />
              ) : (
                <CloudOff className="h-3.5 w-3.5 text-red-500" />
              )}
              <span className={cn("text-xs font-medium", {
                'text-blue-600': syncHealth.status === 'running',
                'text-muted-foreground': syncHealth.status === 'healthy',
                'text-amber-600': syncHealth.status === 'stale',
                'text-red-600': syncHealth.status === 'error',
              })}>
                {syncHealth.label}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Sync Health Alert (only shown if sync is stale or errored) */}
      {syncHealth && (syncHealth.status === 'stale' || syncHealth.status === 'error') && (
        <div className={cn(
          "flex items-center gap-3 rounded-lg border px-4 py-2.5",
          syncHealth.status === 'error'
            ? "border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-800"
            : "border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800"
        )}>
          <CloudOff className={cn("h-4 w-4 flex-shrink-0", syncHealth.status === 'error' ? "text-red-600" : "text-amber-600")} />
          <span className={cn("text-sm font-medium flex-1", syncHealth.status === 'error' ? "text-red-800 dark:text-red-200" : "text-amber-800 dark:text-amber-200")}>
            {syncHealth.status === 'error'
              ? t('dashboard.syncAlert.error', { error: syncStatus?.lastError ?? 'unknown error' })
              : t('dashboard.syncAlert.stale')}
          </span>
          <Button variant="outline" size="sm" className="flex-shrink-0" asChild>
            <a href="/production/settings">{t('dashboard.syncAlert.checkSync')}</a>
          </Button>
        </div>
      )}

      {/* Critical Issues Alert (only shown if issues exist) */}
      {totalIssues > 0 && stats && (
        <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 dark:bg-amber-950/30 dark:border-amber-800">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-600" />
          <span className="text-sm text-amber-800 dark:text-amber-200 font-medium flex-1">
            {t('dashboard.issuesAlert', { count: totalIssues })}
            {stats.issues.oosCount > 0 && ` ${t('dashboard.issuesAlert.oos', { count: stats.issues.oosCount })}`}
            {stats.issues.redoPending > 0 && ` ${t('dashboard.issuesAlert.redo', { count: stats.issues.redoPending })}`}
            {stats.issues.stuckBins > 0 && ` ${t('dashboard.issuesAlert.stuckBins', { count: stats.issues.stuckBins })}`}
            {printFileIssues > 0 && ` ${t('dashboard.issuesAlert.printFile', { count: printFileIssues })}`}
          </span>
          <Button variant="outline" size="sm" className="flex-shrink-0" asChild>
            <a href="/production/issues">{t('dashboard.viewIssues')}</a>
          </Button>
        </div>
      )}

      {/* Production Flow - Stage Cards */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">{t('dashboard.productionFlow')}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {/* Mirror the loaded layout exactly (card size + connecting
                  arrows) so nothing reflows when the stats land. */}
              {STAGES.map((stage, idx) => (
                <div key={stage.key} className="flex items-center">
                  <Skeleton className="h-[116px] w-[130px] shrink-0 rounded-lg" />
                  {idx < STAGES.length - 1 && (
                    <ChevronRight className="h-5 w-5 text-muted-foreground mx-1 shrink-0" aria-hidden="true" />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {STAGES.map((stage, idx) => {
                const count = stats?.stages[stage.key] ?? 0;
                const Icon = stage.icon;
                return (
                  <div key={stage.key} className="flex items-center">
                    {/* Stage Card - 130px width for better readability on production floor */}
                    <div className={cn(
                      "flex flex-col items-center justify-center p-4 rounded-lg min-w-[130px] shrink-0 border border-border/50",
                      stage.color
                    )}>
                      <Icon className="h-6 w-6 text-muted-foreground mb-1" aria-hidden="true" />
                      <span className="text-3xl font-bold tabular-nums">{count}</span>
                      <span className="text-sm text-muted-foreground font-medium">{t(stage.labelKey)}</span>
                    </div>
                    {/* Arrow between stages */}
                    {idx < STAGES.length - 1 && (
                      <ChevronRight className="h-5 w-5 text-muted-foreground mx-1 shrink-0" aria-hidden="true" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Today's Activity - MetricCards */}
      <div>
        <h2 className="text-lg font-semibold mb-1">{t('dashboard.todaysActivity')}</h2>
        <p className="text-sm text-muted-foreground mb-3">{t('dashboard.last24Hours')}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {isLoading ? (
            <>
              {/* Same height + corner radius as a rendered MetricCard. */}
              <Skeleton className="h-[120px] rounded-xl" />
              <Skeleton className="h-[120px] rounded-xl" />
              <Skeleton className="h-[120px] rounded-xl" />
            </>
          ) : (
            <>
              <MetricCard
                variant="total"
                title={t('dashboard.ordersShipped')}
                value={stats?.today.ordersShipped.toString() ?? '0'}
                icon={Truck}
              />
              <MetricCard
                variant="production"
                title={t('dashboard.itemsProcessed')}
                value={stats?.today.garmentsProcessed.toString() ?? '0'}
                icon={Package}
              />
              <MetricCard
                variant="info"
                title={t('dashboard.batchesCompleted')}
                value={stats?.today.batchesCompleted.toString() ?? '0'}
                icon={PackageCheck}
              />
            </>
          )}
        </div>
      </div>

      {/* Historical Performance Chart */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="text-lg">{t('dashboard.historicalPerformance')}</CardTitle>
              <CardDescription>{t('dashboard.dailyTrends')}</CardDescription>
            </div>
            <DashboardDateRangePicker
              value={historyDateRange}
              onChange={setHistoryDateRange}
            />
          </div>
        </CardHeader>
        <CardContent>
          <DashboardHistoryChart
            data={historyData?.history ?? []}
            isLoading={isLoadingHistory}
            dateRangeLabel={`${format(historyDateRange.from, 'MMM d')} - ${format(historyDateRange.to, 'MMM d')}`}
          />
        </CardContent>
      </Card>

      {/* Active Issues (only shown if issues exist) */}
      {stats && totalIssues > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-3">{t('dashboard.activeIssues')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {stats.issues.oosCount > 0 && (
              <Card className="border-red-200 dark:border-red-800">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{t('dashboard.outOfStock')}</p>
                      <p className="text-2xl font-bold text-red-600">{stats.issues.oosCount}</p>
                    </div>
                    <Badge variant="destructive">OOS</Badge>
                  </div>
                </CardContent>
              </Card>
            )}
            {stats.issues.redoPending > 0 && (
              <Card className="border-amber-200 dark:border-amber-800">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{t('dashboard.redoPending')}</p>
                      <p className="text-2xl font-bold text-amber-600">{stats.issues.redoPending}</p>
                    </div>
                    <Badge className="bg-amber-500 hover:bg-amber-600">Redo</Badge>
                  </div>
                </CardContent>
              </Card>
            )}
            {stats.issues.stuckBins > 0 && (
              <Card className="border-orange-200 dark:border-orange-800">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{t('dashboard.stuckBins')}</p>
                      <p className="text-2xl font-bold text-orange-600">{stats.issues.stuckBins}</p>
                    </div>
                    <Badge className="bg-orange-500 hover:bg-orange-600">Stuck</Badge>
                  </div>
                </CardContent>
              </Card>
            )}
            {printFileIssues > 0 && (
              <Card className="border-orange-200 dark:border-orange-800">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{t('dashboard.printFileIssues')}</p>
                      <p className="text-2xl font-bold text-orange-600">{printFileIssues}</p>
                    </div>
                    <Badge className="bg-orange-500 hover:bg-orange-600">Print Files</Badge>
                  </div>
                </CardContent>
              </Card>
            )}
            {(stats.issues.cancelledCount ?? 0) > 0 && (
              <Card className="border-slate-200 dark:border-slate-700">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{t('dashboard.cancelledItems')}</p>
                      <p className="text-2xl font-bold text-slate-600 dark:text-slate-400">{stats.issues.cancelledCount}</p>
                    </div>
                    <Badge variant="secondary">Cancelled</Badge>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
